/**
 * Гуталын серийн дугаар — ХОЁР хэлбэр.
 *
 *  1. Бизнесийн баркод (11–12 орон) — 2026-09-22-ноос сервер олгодог нь
 *     ЭНЭ. Файлын төгсгөлийн «Бизнесийн баркод» хэсгийг үз.
 *  2. Хуучин серийн дугаар (10 орон + Luhn) — өмнө хэвлэгдсэн шошго
 *     хүчинтэй хэвээр, УНШИГДАНА. Цуглуулагчийн мужийн зам үүнийг
 *     олгосоор байна (Оросын урсгал өөрчлөгдөхөд хасагдана).
 *
 * Хуучин хэлбэр:
 *
 *   26 0000147 3
 *   │  │       └── Luhn шалгах орон
 *   │  └────────── дараалал, 7 орон (жилд 10 сая хүртэл)
 *   └───────────── он (2 орон)
 *
 * ДҮРЭМ (хуучин хэлбэрийнх)
 *  - 10 орон, ЗӨВХӨН тоо (Code128-C нягт кодлоно)
 *  - Размер, загвар, брэнд оруулахгүй — утга шингээсэн код давхардана
 *  - Дахин ашиглахгүй — буцаагдсан гутлын код өөр гуталд очихгүй
 *  - Дараалал Postgres sequence-ээс (`item_serial_seq_YYYY`)
 */
export const SERIAL_LENGTH = 10;
export const SEQUENCE_DIGITS = 7;
/** Luhn шалгах орон тооцоолно. */
export function luhnCheckDigit(digits) {
    if (!/^\d+$/.test(digits)) {
        throw new Error(`luhnCheckDigit: зөвхөн цифр байх ёстой, ирсэн: "${digits}"`);
    }
    let sum = 0;
    let alt = true; // хамгийн баруун талаас эхэлж 2 дахин нэмэгдүүлнэ
    for (let i = digits.length - 1; i >= 0; i--) {
        let d = Number(digits[i]);
        if (alt) {
            d *= 2;
            if (d > 9)
                d -= 9;
        }
        sum += d;
        alt = !alt;
    }
    return (10 - (sum % 10)) % 10;
}
/**
 * Серийн дугаар угсарна.
 * @param year    2 оронтой он (26 = 2026)
 * @param sequence Postgres sequence-ийн утга (1-ээс эхэлнэ)
 */
export function buildSerial(year, sequence) {
    if (!Number.isInteger(year) || year < 0 || year > 99) {
        throw new Error(`buildSerial: он 0..99 хооронд байх ёстой, ирсэн: ${year}`);
    }
    if (!Number.isInteger(sequence) || sequence < 1) {
        throw new Error(`buildSerial: дараалал 1-ээс их бүхэл тоо байх ёстой, ирсэн: ${sequence}`);
    }
    const max = 10 ** SEQUENCE_DIGITS - 1;
    if (sequence > max) {
        throw new Error(`buildSerial: дараалал ${max}-аас хэтэрлээ (${sequence}). Шинэ sequence хэрэгтэй.`);
    }
    const base = `${String(year).padStart(2, '0')}${String(sequence).padStart(SEQUENCE_DIGITS, '0')}`;
    return base + luhnCheckDigit(base);
}
/** Хуучин 10 оронтой серийн дугаар мөн эсэх (формат + Luhn). */
export function isLegacySerial(serial) {
    if (typeof serial !== 'string')
        return false;
    const s = normalizeSerialInput(serial);
    if (s.length !== SERIAL_LENGTH || !/^\d{10}$/.test(s))
        return false;
    const base = s.slice(0, -1);
    const check = Number(s.slice(-1));
    return luhnCheckDigit(base) === check;
}
/**
 * Гутлын код хүчинтэй эсэх — бизнесийн баркод ЭСВЭЛ хуучин серийн.
 *
 * ⚠️ Түр код (`9…`, 10 орон + Luhn) ч энд ҮНЭН гарна. Уншуулсан кодыг
 *    эхлээд `isTempCode`-оор шалгана (CLAUDE.md §Түр баркод).
 */
export function isValidSerial(serial) {
    return isLegacySerial(serial) || parseBarcode(serial) !== null;
}
/**
 * Хүний оруулсан утгыг цэвэрлэнэ.
 * Зай, зураас, доогуур зураас хасна. Сканнер заримдаа CR/LF нэмдэг.
 */
export function normalizeSerialInput(raw) {
    return raw.trim().replace(/[\s\-_]/g, '');
}
/**
 * Гутлын код биш уншилт — цифр, зай, зураасаас өөр тэмдэгттэй (үсэг, `:`,
 * `/`, `.` …). Шинэ шошгон дээрх QR нь зүгээр л Facebook-ийн холбоос
 * (2026-11-03): касс түүнийг ЧИМЭЭГҮЙ алгасна, «код буруу» гэж хэлэхгүй.
 */
export function isForeignScan(raw) {
    return /[^0-9\s\-_]/.test(raw.trim());
}
/** Хуучин серийн дугаарыг задална. Хүчингүй (эсвэл 11–12 оронтой) бол null. */
export function parseSerial(serial) {
    const s = normalizeSerialInput(serial);
    if (!isLegacySerial(s))
        return null;
    return {
        year: Number(s.slice(0, 2)),
        sequence: Number(s.slice(2, 2 + SEQUENCE_DIGITS)),
    };
}
/**
 * Тухайн оны sequence-ийн БҮТЭН нэр (schema-тайгаа).
 *
 * `core.` угтвар заавал: Prisma нь multiSchema горимд ажилладаг тул
 * search_path-д найдвал sequence өөр schema-аас олдож болзошгүй.
 * Тодорхойлолт: `infra/sql/002_raw_sql.sql`.
 */
export function sequenceName(fullYear) {
    return `core.item_serial_seq_${fullYear}`;
}
const DOC_PAD = {
    S: 6, // S-2026-004521   захиалга
    P: 3, // P-2026-014      худалдан авалт
    TR: 4, // TR-2026-0087    шилжүүлэлт
    SC: 3, // SC-2026-003     тооллого
    WC: 4, // WC-2029-0012    баталгааны нэхэмжлэл
    SUP: 3, // SUP-001         нийлүүлэгч (онгүй)
    RT: 4, // RT-2026-0031    буцаалт
    WO: 4, // WO-2026-0007    актлалт
};
export function buildDocCode(prefix, fullYear, sequence) {
    const pad = DOC_PAD[prefix];
    const num = String(sequence).padStart(pad, '0');
    if (prefix === 'SUP')
        return `SUP-${num}`;
    return `${prefix}-${fullYear}-${num}`;
}
// ------------------------------------------------------------
//  Түр код — нийлүүлэлтийн үеийн таних тэмдэг
// ------------------------------------------------------------
/**
 * Орост хэвлэгддэг ТҮР баркод.
 *
 *   9 07 000142 8
 *   │ │  │      └── Luhn шалгах орон
 *   │ │  └───────── дараалал, 6 орон (төхөөрөмж тутам 1 сая)
 *   │ └──────────── төхөөрөмжийн дугаар (`DEV-COL-07` → 07)
 *   └────────────── угтвар 9
 *
 * ЯАГААД ХЭРЭГТЭЙ ВЭ
 *  Дүрэм 6-аар серийн дугаар АГУУЛАХАД олгогддог — Орост холбоо
 *  3 долоо хоног байхгүй тул хэд хэдэн утас офлайнаар дугаар олговол
 *  давхардана. Гэвч гутал дээр биет таних тэмдэггүй бол агуулах
 *  «юуг ч уншуулж» эхлэх боломжгүй. Түр код нь тэр цоорхойг нөхнө:
 *  Орост хэвлэгдэж гутлан дотор явна, агуулах уншуулмагц тухайн
 *  агуулахад бүртгэгдэж, ЖИНХЭНЭ серийн дугаар олгогдоно.
 *
 * ⚠️ УГТВАР 9 нь МӨРГӨЛДӨХГҮЙ баталгаа. Жинхэнэ дугаар 2 оронтой
 *    ОНООР эхэлдэг (26–36 = 2026–2036, sequence нь урьдчилж
 *    үүссэн). 9x гэсэн он 2090 хүртэл гарахгүй тул нэг ширхэг
 *    цифрээр хоёр орон зайг бүрэн салгана.
 *
 * ⚠️ 10 орон, зөвхөн тоо — жинхэнэ дугаартай ЯГ ИЖИЛ хэлбэр.
 *    Ингэснээр Code128-C кодлолт, 40мм шошго, keyboard-wedge
 *    сканнер гурвуулаа ялгаагүй ажиллана.
 */
export const TEMP_CODE_PREFIX = '9';
export const TEMP_DEVICE_DIGITS = 2;
export const TEMP_SEQUENCE_DIGITS = 6;
export function buildTempCode(deviceNo, sequence) {
    if (!Number.isInteger(deviceNo) || deviceNo < 0 || deviceNo > 99) {
        throw new Error(`buildTempCode: төхөөрөмжийн дугаар 0..99, ирсэн: ${deviceNo}`);
    }
    const max = 10 ** TEMP_SEQUENCE_DIGITS - 1;
    if (!Number.isInteger(sequence) || sequence < 1 || sequence > max) {
        throw new Error(`buildTempCode: дараалал 1..${max}, ирсэн: ${sequence}`);
    }
    const base = TEMP_CODE_PREFIX +
        String(deviceNo).padStart(TEMP_DEVICE_DIGITS, '0') +
        String(sequence).padStart(TEMP_SEQUENCE_DIGITS, '0');
    return base + luhnCheckDigit(base);
}
/**
 * Түр код мөн үү.
 *
 * ⚠️ `isValidSerial`-тай ХАРИЛЦАН ҮГҮЙСГЭГЧ БИШ: хоёулаа 10 орон +
 *    Luhn шалгадаг тул түр код `isValidSerial`-ыг ч давна. Ялгах
 *    цорын ганц зүйл нь угтвар — тиймээс уншуулсан кодыг эхлээд
 *    ЭНДЭЭС шалгана, дараа нь жинхэнэ дугаар гэж үзнэ.
 */
export function isTempCode(code) {
    const s = normalizeSerialInput(code);
    // ⚠️ `isLegacySerial`, `isValidSerial` БИШ: 9x угтвартай нийлүүлэгчийн
    //    11 оронтой баркод түр код гэж андуурагдах ёсгүй.
    return s.startsWith(TEMP_CODE_PREFIX) && isLegacySerial(s);
}
/** Түр кодыг задална. Хүчингүй бол null. */
export function parseTempCode(code) {
    const s = normalizeSerialInput(code);
    if (!isTempCode(s))
        return null;
    return {
        deviceNo: Number(s.slice(1, 1 + TEMP_DEVICE_DIGITS)),
        sequence: Number(s.slice(1 + TEMP_DEVICE_DIGITS, 1 + TEMP_DEVICE_DIGITS + TEMP_SEQUENCE_DIGITS)),
    };
}
/**
 * `DEV-COL-07` → 7. Төхөөрөмжийн кодын сүүлийн тоог авна.
 *
 * ⚠️ Seed-ийн гар кодууд (`DEV-RU-01`) ч ижил хэлбэртэй тул
 *    ажиллана. Тоо олдохгүй бол null — дуудагч түр код үүсгэхээс
 *    татгалзана, санамсаргүй 0 болгож давхардуулахгүй.
 */
export function deviceNoFromCode(deviceCode) {
    const m = /(\d+)$/.exec(deviceCode.trim());
    if (!m)
        return null;
    const n = Number(m[1]);
    return Number.isInteger(n) && n >= 0 && n <= 99 ? n : null;
}
// ------------------------------------------------------------
//  Бизнесийн баркод — дэлгүүрийн шошгоны загвар (2026-09-22)
// ------------------------------------------------------------
/**
 * Бизнест аль хэдийн хэрэглэгдэж буй шошгоны загвар.
 *
 *   44 6 09 19 37 03
 *   │  │ │  │  │  └── дэс дугаар 01–99, дараа нь 100–999 (нийлүүлэгч+өдөр+размер ДОТОР)
 *   │  │ │  │  └───── размер
 *   │  │ │  └──────── өдөр
 *   │  │ └─────────── сар
 *   │  └───────────── оны сүүлийн орон (6 = 2026)
 *   └──────────────── нийлүүлэгчийн угтвар (`suppliers.barcode_prefix`)
 *
 * Эхний 9 орон (`stem`) = нийлүүлэгч+огноо+размер. Дэс дугаар stem тус
 * бүрт 01-ээс эхэлнэ.
 *
 * ⚠️ 99-өөс хойш дэс дугаар 3 оронтой болж код 12 оронтой болно
 *    (`446091937103`) — бизнес гараар шошгожуулахдаа ингэдэг байсан
 *    (2026-09-27, эхний үлдэгдлийн бүртгэлд илэрсэн). 12 оронтой кодын
 *    дэс дугаар ҮРГЭЛЖ 100–999: `…37009` мэт тэгээр эхэлсэн 3 орон нь
 *    `…3709`-тэй НЭГ утгатай атлаа өөр мөр болох тул хүлээн авахгүй.
 *
 * ⚠️ Шалгах орон БАЙХГҮЙ (захиалагчийн загвар). Гараар буруу шивсэн код
 *    өөр БОДИТ гутлыг олж болзошгүй — дэлгэц олдсон гутлын размер,
 *    нийлүүлэгчийг харуулна.
 *
 * ⚠️ Он НЭГ оронтой: 2036 оны код 2026-оныхтой ижил эхэлнэ. Давхардал нь
 *    сервер дэс дугаарыг stem доторх ХАМГИЙН ИХ-ээс үргэлжлүүлдгээр
 *    (он харгалзахгүй) хаагдана — `nextBarcodes`. `items.serial`
 *    @unique нь хоёр дахь хана.
 */
/** Гараар шивэх талбарын дээд урт — 3 оронтой дэс дугаартай баркодынх. */
export const SERIAL_MAX_LENGTH = 12;
export const BARCODE_SEQ_MAX = 999;
/** Нийлүүлэгчийн угтвар хүчинтэй эсэх — яг 2 цифр. */
export function isValidSupplierPrefix(prefix) {
    return /^\d{2}$/.test(prefix);
}
/** Баркодын эхний 9 орон: нийлүүлэгч + огноо + размер. */
export function barcodeStem(input) {
    const { supplierPrefix, yearDigit, month, day, size } = input;
    if (!isValidSupplierPrefix(supplierPrefix)) {
        throw new Error(`barcodeStem: нийлүүлэгчийн угтвар 2 цифр байх ёстой, ирсэн: "${supplierPrefix}"`);
    }
    const inRange = (n, lo, hi) => Number.isInteger(n) && n >= lo && n <= hi;
    if (!inRange(yearDigit, 0, 9))
        throw new Error(`barcodeStem: оны орон 0..9, ирсэн: ${yearDigit}`);
    if (!inRange(month, 1, 12))
        throw new Error(`barcodeStem: сар 1..12, ирсэн: ${month}`);
    if (!inRange(day, 1, 31))
        throw new Error(`barcodeStem: өдөр 1..31, ирсэн: ${day}`);
    if (!inRange(size, 1, 99))
        throw new Error(`barcodeStem: размер 2 оронд багтах ёстой, ирсэн: ${size}`);
    const p2 = (n) => String(n).padStart(2, '0');
    return `${supplierPrefix}${yearDigit}${p2(month)}${p2(day)}${p2(size)}`;
}
export function buildBarcode(parts) {
    const { seq } = parts;
    if (!Number.isInteger(seq) || seq < 1 || seq > BARCODE_SEQ_MAX) {
        throw new Error(`buildBarcode: дэс дугаар 1..${BARCODE_SEQ_MAX}, ирсэн: ${seq}`);
    }
    return barcodeStem(parts) + String(seq).padStart(2, '0');
}
/**
 * Бизнесийн баркодыг задална. Хэлбэр буруу (сар 13, өдөр 00, дэс
 * дугаар 00 г.м.) бол null.
 *
 * ⚠️ Нийлүүлэгч бүртгэлтэй эсэхийг ЭНД шалгахгүй — тэр нь DB-ийн
 *    мэдээлэл (`suppliers.barcode_prefix`).
 */
export function parseBarcode(code) {
    if (typeof code !== 'string')
        return null;
    const s = normalizeSerialInput(code);
    if (!/^\d{11,12}$/.test(s))
        return null;
    const parts = {
        supplierPrefix: s.slice(0, 2),
        yearDigit: Number(s[2]),
        month: Number(s.slice(3, 5)),
        day: Number(s.slice(5, 7)),
        size: Number(s.slice(7, 9)),
        seq: Number(s.slice(9)),
    };
    if (parts.month < 1 || parts.month > 12)
        return null;
    if (parts.day < 1 || parts.day > 31)
        return null;
    if (parts.size < 1 || parts.seq < 1)
        return null;
    // 12 орон = 3 оронтой дэс дугаар, үргэлж 100+ (хэсгийн толгойг үз)
    if (s.length === 12 && parts.seq < 100)
        return null;
    return parts;
}
/**
 * Улаанбаатарын цагаар он, сар, өдөр. Баркод дахь огноо нь бизнесийн
 * өдөр — UTC-ээр авбал өглөө 8 цагаас өмнө хүлээн авсан гутал
 * ӨЧИГДРИЙН огноотой болно.
 */
export function ulaanbaatarDateParts(d) {
    const [year, month, day] = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Ulaanbaatar',
        year: 'numeric', month: '2-digit', day: '2-digit',
    })
        .format(d)
        .split('-')
        .map(Number);
    return { year, month, day };
}
/**
 * Оны нэг оронг бүтэн он болгоно — `today`-аас хойш биш, хамгийн ойрын.
 * 2026 онд: 6 → 2026, 5 → 2025, 7 → 2017.
 */
export function barcodeFullYear(yearDigit, today) {
    const y = ulaanbaatarDateParts(today).year;
    return y - ((((y - yearDigit) % 10) + 10) % 10);
}
/** Тухайн өдрийн (УБ цагаар) нийлүүлэгч + размерын stem. */
export function barcodeStemForDay(supplierPrefix, size, day) {
    const p = ulaanbaatarDateParts(day);
    return barcodeStem({ supplierPrefix, yearDigit: p.year % 10, month: p.month, day: p.day, size });
}
/**
 * Stem дотор дараагийн N баркод.
 *
 * `taken` — тухайн stem-ээр эхэлсэн, DB-д байгаа кодууд. ХАМГИЙН ИХ
 * дээр нь үргэлжилнэ, нүх нөхөхгүй: эхний үлдэгдлийн шошго гараар
 * хэвлэгдсэн тул нүх нь «хэвлэгдсэн ч бүртгэгдээгүй» гутал байж болно.
 *
 * 99-өөс хойш 3 оронтой болно (`…37100`). 999-өөс хэтэрвэл null —
 * дуудагч ойлгомжтой алдаа өгнө.
 */
export function nextBarcodes(stem, taken, n) {
    let max = 0;
    for (const code of taken) {
        if (!code.startsWith(stem))
            continue;
        const parts = parseBarcode(code);
        if (parts)
            max = Math.max(max, parts.seq);
    }
    if (max + n > BARCODE_SEQ_MAX)
        return null;
    return Array.from({ length: n }, (_, i) => stem + String(max + 1 + i).padStart(2, '0'));
}
//# sourceMappingURL=serial.js.map